import {
  apiDeleteRequest,
  apiGetRequest,
  apiPostRequest,
  generateApiRequestHeaders,
} from './apiHttp';
import { ListingSearchState } from 'store/listingsSearchParams/types';
import { environmentVariables } from 'utils/environmentVariables';
import {
  ListingResultsSearchCloud,
  ListingTableData,
  QueueParams,
} from 'shared/interfaces';
import { requestHasErrors } from 'utils/http/http';

interface ParamsOptions {
  version: string;
  endpoint: string;
  region: string;
}

interface SearchBodyParams {
  options: ParamsOptions;
  params: {
    queryParser: string;
    query: string;
    size: number;
    sort?: string;
    start?: number;
  };
}

const apiListing = {
  mine: async (
    pageSize = 100,
    workflowStatusId?: number,
    litterId?: number
  ) => {
    let appendParameters = `pageSize=${pageSize}`;
    if (workflowStatusId)
      appendParameters += `&workflowStatusId=${workflowStatusId}`;
    if (litterId) appendParameters += `&litterId=${litterId}`;

    const resp = await apiGetRequest(`listing/mine`, appendParameters);

    if (resp.json?.errors) {
      throw new Error(`Couldn't get mine listings`);
    }
    return resp.json?.listings;
  },
  queue: async (params: QueueParams) => {
    const {
      page = 1,
      pageSize = 100,
      sortBy,
      sortDirection,
      editType,
      listingStatusId,
      inventoryStatusId,
      lockedByUser,
      breed,
      breederName,
    } = params;

    // TODO by @JuanVilla: This way of building parameters has to be inproved eventually (it has to be dynamic) to avoid having
    // a condition statement (if) for each param (bad idea)
    let parameters = `page=${page}&pageSize=${pageSize}`;

    if (sortBy && sortDirection) {
      parameters += `&sortBy=${sortBy}&sortDirection=${sortDirection}`;
    }
    if (editType) {
      parameters += `&editType=${
        editType === 'new_listings' ? 'new' : editType
      }`;
    }
    if (listingStatusId) {
      parameters += `&${listingStatusId}`;
    }
    if (inventoryStatusId) {
      parameters += `&${inventoryStatusId}`;
    }
    if (lockedByUser) {
      parameters += `&lockedByUser=${lockedByUser}`;
    }
    if (breederName) {
      parameters += `&breederName=${breederName}`;
    }
    if (breed) {
      parameters += `&breedName=${breed}`;
    }

    const resp = await apiGetRequest(`listing/queue`, parameters);

    if (resp.json?.errors) {
      throw new Error(`Couldn't get queue listings`);
    }
    return {
      listings: resp.json?.listings,
      itemsTotal: resp.json?.paginationMeta?.itemsTotal || 0,
      itemsTotalWithoutLocked:
        resp.json?.paginationMeta?.itemsTotalWithoutLocked || 0,
    };
  },
  postQueue: async (listingId: number) => {
    const resp = await apiPostRequest(`listing/queue`, { listingId });

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't enqueue listing with id: ${listingId}`);
    }

    return resp?.json?.success;
  },
  release: async (listingIds: number[]) => {
    const resp = await apiPostRequest(`listing/release`, { listingIds });

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't released listings`);
    }
  },
  cloudSearch: async (searchParams: ListingSearchState, fromSearch = false) => {
    const headers = generateApiRequestHeaders();

    const { sort } = searchParams;
    let sortTerm = '';

    if (sort?.order) {
      const order = sort?.order === 'descend' ? 'desc' : 'asc';
      sortTerm = `${sort?.columnKey} ${order}`;
    }

    const isQuickFilter =
      searchParams.status === 'pendingBreeder' ||
      searchParams.status === 'available' ||
      searchParams.status === 'unavailable';

    let request: Response;

    const parameters = {
      sort: sortTerm,
      filters: searchParams.filter,
      page: searchParams.page ?? 1,
    };

    if (
      searchParams.query !== null &&
      (fromSearch || searchParams.status === 'search')
    ) {
      const query = searchParams.query;
      const [key, value] = query.split(':');
      parameters.filters = parameters.filters || {};
      parameters.filters[key] = [value];
    }

    let endpointPath = '/api/pet/listings/search';
    if (isQuickFilter && !fromSearch) {
      switch (searchParams.status) {
        case 'pendingBreeder':
          endpointPath = '/api/pet/listings/pending-breeder';
          break;
        case 'available':
          endpointPath = '/api/pet/listings/available';
          break;
        case 'unavailable':
          endpointPath = '/api/pet/listings/unavailable';
          break;
        default:
          endpointPath = '/api/pet/listings/search';
      }

      const url = `${environmentVariables.REACT_APP_API_HOST}${endpointPath}?application=${environmentVariables.REACT_APP_APPLICATION_ID}`;
      request = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(parameters),
      });
    } else {
      request = await fetch(
        `${environmentVariables.REACT_APP_API_HOST}/api/pet/listings/search?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(parameters),
        }
      );
    }

    const resp = await request.clone().json();

    if (resp.errors) {
      throw new Error(`Couldn't get listings`);
    }

    let listings: ListingTableData[] = [];
    let listingsResults = [];
    let totalListingsResults = 0;

    if (resp.result?.hits?.hit && resp.result?.hits?.found) {
      listingsResults = resp.result?.hits?.hit;
      totalListingsResults = resp.result?.hits?.found;

      listings = listingsResults.map((l: ListingResultsSearchCloud) => {
        const { fields } = l;
        return Object.keys(fields).reduce((acc, key) => {
          return {
            ...acc,
            key: fields.listingId[0],
            [key]: fields[key][0],
          };
        }, {});
      });
    }

    return { listings, totalListingsResults };
  },
  assignmentSummary: async () => {
    const resp = await apiGetRequest(`listing/pending-assignment-summary`);

    if (resp.json?.errors) {
      throw new Error(`Couldn't get assignments summary`);
    }

    return resp?.json?.data;
  },
  postpone: async (
    listingId: number,
    reasons: number[],
    otherReason: string
  ) => {
    const bodyPayload = {
      listings: {
        [listingId]: {
          otherReason,
          reasons,
        },
      },
    };
    const resp = await apiPostRequest(`listing/postpone`, bodyPayload);

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't released listings`);
    }

    return resp?.json?.listings?.success[listingId];
  },
  unPostpone: async (listingIds: number[]) => {
    const resp = await apiPostRequest(`listing/un-postpone`, { listingIds });

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't un-postpone the listing`);
    }

    return resp?.json?.listings?.success;
  },
  reassign: async (
    listingId: number,
    toUserId: number,
    otherReason: string,
    comment: string
  ) => {
    const otherReasonId = 16;
    const bodyPayload = {
      listing_ids: [listingId],
      payload: {
        toUserId,
        reasons: [otherReasonId],
        otherReason,
        comment,
      },
    };
    const resp = await apiPostRequest(`listing/reassign`, bodyPayload);

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't reassign listing`);
    }

    if (!resp?.json?.listings?.success[listingId]) {
      throw new Error(resp?.json?.listings?.error[listingId][0]);
    }

    return resp?.json?.listings?.success[listingId];
  },
  approve: async (listingId: number) => {
    const bodyPayload = {
      listingIds: listingId,
    };
    const resp = await apiPostRequest(`listing/approve`, bodyPayload);

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't reassign listing`);
    }

    return resp?.json?.listings?.success[listingId];
  },
  lock: async (
    listingIds: number[],
    skipRemoved: boolean,
    skipSold: boolean
  ) => {
    const resp = await apiPostRequest(`listing/lock`, {
      listingIds,
      skipRemoved,
      skipSold,
    });

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't lock the listing`);
    }

    return resp?.json?.listings;
  },
  getLitterListings: async (litterId: number) => {
    const resp = await apiGetRequest(
      `listing`,
      `pageSize=100&litterId=${litterId}`
    );

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't get litter listings`);
    }

    return resp?.json?.listings;
  },
  accept: async (listingIds: number[]) => {
    const resp = await apiPostRequest('listing/accept', { listingIds });

    if (requestHasErrors(resp)) {
      throw new Error(`Couldn't accept listing`);
    }

    return;
  },
  rejectPuppyPendingEdits: async (puppyId: number) => {
    return apiDeleteRequest(`puppy/${puppyId}/reject-pending-edits`);
  },
};

export default apiListing;
